// Workflow definitions, expressed in the uniform block model (schema.ts).
//
// These are the seed/system workflows shown on the Workflows screen, authored
// with the typed builder helpers (`trigger`/`condition`/`decision`/`execution`/
// `delay`). The exact `{ type, parameters }` envelope a future builder UI would
// emit. The "How it works" diagram is generated from these via `toDisplaySteps`,
// so the depiction and the engine's input never drift.

import {
  trigger,
  condition,
  decision,
  execution,
  delay,
  type WorkflowDefinition,
} from "./schema.ts";

/**
 * Trial-to-paid conversion. Linear flow:
 *   Trigger (first visit completed) → Execution (send package invite).
 *
 * Fires when a customer's FIRST booking is marked completed. v1 sends a templated
 * invite; an AI-crafted, package-specific offer (a Decision node feeding the
 * Execution) is the planned upgrade. Kept out of the diagram for now so the
 * depiction matches what the engine actually runs.
 */
export const TRIAL_TO_PAID: WorkflowDefinition = {
  id: "trial-to-paid",
  key: "trial_to_paid",
  name: "Trial-to-paid conversion",
  description:
    "When a customer completes their first visit, invites them to sign up for a long-term package while the good impression is fresh.",
  version: 3,
  nodes: [
    trigger({
      id: "trigger",
      event: "trial_or_first_visit_completed",
      label: "When a first visit is completed",
      description: "A customer's first booking is marked completed — their trial or first visit.",
    }),
    execution({
      id: "send-offer",
      action: "send_message",
      message: {
        source: "static",
        value:
          "Hi {{customer_name}}, thank you for joining us for {{listing_name}}. If you'd like to continue, reply here and we can share the available next-step or package options.",
      },
      emailFallback: true,
      label: "Send message",
      description: "Invites the customer to sign up for a package so they keep coming back.",
    }),
  ],
};

/**
 * Attendance confirmation. This is deliberately separate from no-show
 * recovery: silence never cancels a booking or changes the workflow's customer.
 * The team is notified when no explicit confirmation arrives and decides what
 * to do next.
 */
export const ATTENDANCE_CONFIRMATION: WorkflowDefinition = {
  id: "attendance-confirmation",
  key: "attendance_confirmation",
  name: "Attendance confirmation",
  description:
    "Asks for an explicit CONFIRM reply before an upcoming booking. If no clear confirmation arrives, it reminds the customer once and then alerts the business for a human decision.",
  version: 1,
  nodes: [
    trigger({
      id: "trigger",
      event: "booking_upcoming",
      hoursBefore: 48,
      label: "Booking is 48 hours away",
      description: "An upcoming confirmed booking is 48 hours from its start time.",
    }),
    execution({
      id: "send-48h-ask",
      action: "send_message",
      message: {
        source: "static",
        value:
          "Hi {{customer_name}}, your {{listing_name}} booking is on {{booking_date}} at {{booking_time}}. Please reply CONFIRM to let us know you're attending, or CANCEL if your plans have changed.",
      },
      emailFallback: true,
      label: "Send confirmation request",
      description: "Asks the customer for an unambiguous CONFIRM or CANCEL reply.",
    }),
    delay({
      id: "wait-24h",
      mode: "duration",
      durationMinutes: 24 * 60,
      label: "Wait 24 hours",
      description: "Gives the customer time to reply before one final reminder.",
    }),
    condition({
      id: "confirmed-after-48h",
      label: "If-else condition",
      description: "If the customer already confirmed after the first reminder, no further nudges are needed.",
      exceptions: [
        {
          label: "The customer explicitly confirmed attendance",
          field: "conversation.confirmedAttendance",
          operator: "is_true",
        },
      ],
    }),
    execution({
      id: "send-24h-ask",
      action: "send_message",
      message: {
        source: "static",
        value:
          "Hi {{customer_name}}, we still need confirmation for {{listing_name}} on {{booking_date}} at {{booking_time}}. Please reply CONFIRM if you're attending, or CANCEL if you need help changing the booking.",
      },
      emailFallback: true,
      label: "Send final reminder",
      description: "Sends one final request for an explicit attendance answer.",
    }),
    delay({
      id: "wait-6h-window",
      mode: "duration",
      durationMinutes: 6 * 60,
      label: "Wait 6 hours",
      description: "Gives the customer a final six-hour response window.",
    }),
    condition({
      id: "confirmed-after-24h",
      label: "If-else condition",
      description: "If the customer confirmed within the 6h window, the slot is secure — workflow ends.",
      exceptions: [
        {
          label: "The customer explicitly confirmed after the final reminder",
          field: "conversation.confirmedAttendance",
          operator: "is_true",
        },
      ],
    }),
    execution({
      id: "notify-team",
      action: "notify_owner",
      reason:
        "{{customer_name}} has not explicitly confirmed {{listing_name}} on {{booking_date}} at {{booking_time}}. Please review the booking; Dibs has not cancelled or released it.",
      label: "Ask the team to review",
      description: "Alerts the business; the booking stays untouched until a person decides.",
    }),
  ],
};

/** No-show recovery starts only after staff mark the booking as a no-show. */
export const NO_SHOW_RECOVERY: WorkflowDefinition = {
  id: "no-show-recovery",
  key: "no_show_recovery",
  name: "No-show recovery",
  description:
    "When a booking is marked as a no-show, waits a day and sends one respectful message offering help to book another time.",
  version: 7,
  nodes: [
    trigger({
      id: "trigger",
      event: "no_show",
      label: "Customer is marked as a no-show",
      description: "A staff member records that the customer did not attend.",
    }),
    delay({
      id: "wait-one-day",
      mode: "duration",
      durationMinutes: 24 * 60,
      label: "Wait 24 hours",
      description: "Leaves a respectful gap and avoids colliding with appointment messages.",
    }),
    execution({
      id: "send-rebook",
      action: "send_message",
      message: {
        source: "static",
        value:
          "Hi {{customer_name}}, we missed you at your recent {{listing_name}} booking. If you'd like to rebook, reply here and we'll help you find another suitable time.",
      },
      emailFallback: true,
      label: "Offer help to rebook",
      description: "Sends one low-pressure rebooking invitation to the original customer.",
    }),
  ],
};

/**
 * Idle-lead win-back. Linear flow with a delay and an AI decision:
 *   Trigger → Delay (24h) → Condition → Decision (AI) → Execution.
 */
export const IDLE_LEAD_WINBACK: WorkflowDefinition = {
  id: "idle-lead-winback",
  key: "conversation_follow_up",
  name: "Idle-lead win-back",
  description:
    "When a new lead messages and you reply but they go quiet, sends one friendly nudge to re-engage them.",
  version: 3,
  nodes: [
    trigger({
      id: "trigger",
      event: "lead_replied_then_went_quiet",
      label: "New lead goes quiet",
      description: "A new lead messaged and you replied, but they haven't responded since.",
    }),
    delay({
      id: "wait",
      mode: "duration",
      durationMinutes: 24 * 60,
      label: "Wait 24 hours",
      description: "Gives the lead time to reply before sending a nudge.",
    }),
    condition({
      id: "cold-check",
      label: "If-else condition",
      description: "Confirms the lead is still cold and that messaging rules allow a nudge.",
      exceptions: [
        {
          label: "The lead already replied after this workflow started",
          field: "conversation.hasReplied",
          operator: "is_true",
        },
        {
          label: "They opted out of marketing messages",
          field: "customer.optedOutOfMarketing",
          operator: "is_true",
        },
        {
          label: "Another workflow already messaged them in the last 3 days",
          field: "customer.lastAutomationMessagedDaysAgo",
          operator: "within_days",
          value: 3,
        },
      ],
    }),
    decision({
      id: "craft-nudge",
      label: "Craft message",
      description: "The AI writes a short, friendly message to revive the conversation.",
      goal:
        "Write a single warm, non-pushy follow-up message to re-engage the lead, referencing the service they were interested in.",
      output: [
        { key: "message", type: "string", description: "The re-engagement message to send." },
      ],
      tone: "Friendly, light, no pressure.",
      mustAvoid: ["pressure tactics", "false urgency"],
    }),
    execution({
      id: "send-nudge",
      action: "send_message",
      message: { source: "decision", nodeId: "craft-nudge", field: "message" },
      label: "Send message",
      description: "Sends the re-engagement nudge to the lead.",
    }),
  ],
};

/**
 * Crisis outreach protection. Case creation itself is durable and immediate;
 * this visible default workflow applies the cross-workflow safety pause and a
 * profile tag only when bounded triage says the situation is sensitive.
 * Owners can duplicate it and add their own notifications or routing without
 * being able to make AI close a clinical/safety case.
 */
export const CRISIS_OUTREACH_PROTECTION: WorkflowDefinition = {
  id: "crisis-outreach-protection",
  key: "crisis_management",
  name: "Crisis outreach protection",
  description:
    "When a sensitive complaint or concern becomes a case, pauses ordinary sales follow-ups and marks the customer for human care while the team owns the resolution.",
  version: 1,
  nodes: [
    trigger({
      id: "trigger",
      event: "case_opened",
      label: "A case is opened",
      description: "Dibs AI, a workflow or a team member creates a trackable customer case.",
    }),
    condition({
      id: "sensitive-case",
      label: "If-else condition",
      description: "Continues only when the case should silence proactive sales and follow-ups.",
      exceptions: [
        {
          label: "The case does not require an outreach pause",
          field: "case.outreachPauseRecommended",
          operator: "is_false",
        },
      ],
    }),
    execution({
      id: "pause-outreach",
      action: "pause_outreach",
      reason: "Open {{case_reference}} ({{case_category}}, {{case_severity}}): {{concern_summary}}",
      holdDays: 365,
      label: "Pause ordinary outreach",
      description: "Stops proactive sales and follow-up workflows until a human resolves the case.",
    }),
    execution({
      id: "tag-human-care",
      action: "add_tag",
      tag: "case-open-human-care",
      label: "Mark for human care",
      description: "Adds a visible profile tag so other teams can recognise the open case.",
    }),
  ],
};

/**
 * Universal complaint recovery intake. The source connectors all normalize
 * into one complaint record before this workflow starts. Stage 1 deliberately
 * performs only safe internal actions: it confirms the linked case and alerts
 * the owner. Later reply and recovery steps remain human-approved.
 */
export const COMPLAINT_RECOVERY: WorkflowDefinition = {
  id: "complaint-recovery",
  key: "complaint_recovery",
  name: "Complaint recovery",
  description:
    "Turns a new Google review or complaint email into one trackable, human-owned recovery case and alerts the business before anything is promised to the customer.",
  version: 3,
  nodes: [
    trigger({
      id: "trigger",
      event: "complaint_received",
      label: "A complaint is received",
      description: "A new complaint arrives from a connected review or email source.",
    }),
    execution({
      id: "ensure-case",
      action: "open_case",
      reason: "{{complaint_source}} complaint {{complaint_reference}}: {{complaint_summary}}",
      caseCategory: "service",
      caseSeverity: "normal",
      assignedRole: "owner",
      acknowledgeWithinMinutes: 60,
      resolveWithinMinutes: 24 * 60,
      label: "Create one recovery case",
      description: "Keeps the complaint, owner, deadlines and response history together without creating duplicates.",
    }),
    execution({
      id: "notify-owner",
      action: "notify_owner",
      reason:
        "New {{complaint_source}} complaint {{complaint_reference}}{{complaint_rating_text}}. {{complaint_summary}} Open {{case_reference}} to review it.",
      label: "Alert the business",
      description: "Tells the owner what arrived and which case needs a human response.",
    }),
    decision({
      id: "draft-response",
      label: "Draft a careful response",
      description: "Dibs AI acknowledges the customer's specific concern without inventing facts or promising compensation.",
      goal:
        "Draft a concise service-recovery response to this complaint. Acknowledge the specific experience in the customer's own words, apologise for the experience, and invite a private conversation so the team can investigate. Never diagnose, dispute the customer, admit legal liability, or promise a refund, discount, treatment result or compensation.",
      output: [
        { key: "message", type: "string", description: "The response for the owner to review." },
      ],
      tone: "Human, calm, empathetic and specific; 2 to 4 short sentences.",
      mustAvoid: ["discount", "promo code", "refund", "guarantee", "diagnosis", "legal liability"],
    }),
    execution({
      id: "approve-response",
      action: "reply_to_complaint",
      message: { source: "decision", nodeId: "draft-response", field: "message" },
      requiresApproval: true,
      label: "Approve and send the response",
      description: "Places the exact draft in the approval queue. Google or email receives nothing until a person approves it.",
    }),
    condition({
      id: "offer-eligible",
      label: "Check the recovery policy",
      description: "Continues only for an identified customer and a non-sensitive issue within the owner's rating, category and frequency caps.",
      exceptions: [
        {
          label: "The complaint does not safely qualify for a recovery offer",
          field: "complaint.recoveryOfferEligible",
          operator: "is_false",
        },
      ],
    }),
    execution({
      id: "approve-offer",
      action: "issue_recovery_offer",
      requiresApproval: true,
      label: "Approve a one-use recovery code",
      description: "Creates the capped code only after approval, then emails it privately. Sensitive or anonymous cases never reach this step.",
    }),
  ],
};

/** All system workflows executed by the generic interpreter. */
/**
 * New-enquiry qualification.
 *
 * Deliberately does NOT message the customer. Auto-reply (message/received ->
 * ai-message-processor) already answers inbound messages, so a send here would
 * be the second reply to the same message. This workflow does the part
 * auto-reply does not: read the opening message, judge what the enquiry is
 * worth, label the lead, and put it in front of the owner.
 */
export const NEW_LEAD_QUALIFICATION: WorkflowDefinition = {
  id: "new-lead-qualification",
  key: "lead_qualification",
  name: "New enquiry qualification",
  description:
    "When someone messages you for the first time, the AI works out what they want and how ready they are to book, tags them, and tells you.",
  version: 1,
  nodes: [
    trigger({
      id: "trigger",
      event: "message_received",
      newSenderOnly: true,
      label: "A new enquiry comes in",
      description:
        "Someone messages you from a number that has never contacted you before.",
    }),
    condition({
      id: "worth-qualifying",
      label: "If-else condition",
      description: "Skips enquiries that should not be treated as a new lead.",
      exceptions: [
        {
          label: "They opted out of marketing messages",
          field: "customer.optedOutOfMarketing",
          operator: "is_true",
        },
      ],
    }),
    decision({
      id: "qualify",
      label: "Qualify the enquiry",
      description:
        "The AI reads the opening message and summarises what they want and how ready they are.",
      goal:
        "Read the customer's first message and decide what they are asking about and how close they are to booking. Judge only from what they actually wrote - never invent budget, timing or intent that is not there.",
      output: [
        {
          key: "interest",
          type: "string",
          description:
            "What the enquiry is about, in a few words (e.g. 'pricing for weekly maths tuition').",
        },
        {
          key: "readiness",
          type: "enum",
          description:
            "How ready they are to book, judged only from the message.",
          enumValues: ["ready_to_book", "comparing_options", "just_browsing"],
        },
        {
          key: "summary",
          type: "string",
          description:
            "One or two lines for the owner: what they asked and what would move it forward.",
        },
      ],
      tone: "Factual and brief. This is an internal note, not a customer message.",
      mustAvoid: [
        "guessing budget",
        "inventing details the customer did not give",
      ],
    }),
    execution({
      id: "tag-lead",
      action: "add_tag",
      tag: "New enquiry",
      label: "Add tag",
      description: "Labels the contact so new enquiries can be filtered later.",
    }),
    execution({
      id: "tell-owner",
      action: "notify_owner",
      message: { source: "decision", nodeId: "qualify", field: "summary" },
      reason: "A new enquiry came in and has been qualified.",
      label: "Notify me",
      description:
        "Emails you the qualified enquiry so you can pick up the ones worth chasing.",
    }),
  ],
};

export const WORKFLOW_DEFINITIONS: WorkflowDefinition[] = [
  TRIAL_TO_PAID,
  ATTENDANCE_CONFIRMATION,
  NO_SHOW_RECOVERY,
  IDLE_LEAD_WINBACK,
  NEW_LEAD_QUALIFICATION,
  CRISIS_OUTREACH_PROTECTION,
  COMPLAINT_RECOVERY,
];
